import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { X } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sectionVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: silkEase } },
};

export function Problem() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const { language } = useLanguage();
  const t = translations[language].problem;

  return (
    <section className="py-24 px-6">
      <motion.div
        ref={sectionRef}
        className="max-w-3xl mx-auto"
        variants={sectionVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.h2
          className="text-3xl md:text-4xl mb-10 text-center"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.heading}
        </motion.h2>

        <ul className="flex flex-col gap-3 mb-12">
          {t.items.map((item, i) => (
            <motion.li
              key={i}
              className="flex items-center gap-4 px-5 py-4 rounded-xl"
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface-1)",
              }}
              variants={itemVariants}
            >
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: "rgba(212, 149, 106, 0.10)",
                  color: "var(--gold)",
                }}
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </span>
              <span
                className="text-base md:text-lg leading-snug"
                style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
              >
                {item}
              </span>
            </motion.li>
          ))}
        </ul>

        <motion.p
          className="text-xl md:text-2xl text-center leading-relaxed"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.conclusion}{" "}
          <em style={{ color: "var(--gold)", fontStyle: "italic" }}>{t.conclusionAccent}</em>
        </motion.p>
      </motion.div>
    </section>
  );
}
